import { ProductOutlined } from "@ant-design/icons";
import * as React from "react";
import { loadTranslation } from "src/common/api/apiTranslation";
import { loadDelayedNotify } from "../../Obj2Tab/DelayedNotify/apiDelayedNotify";
import { obj2edit, makeRolesMap } from "../../Obj2Tab/EdObject";

import { loadNotifyTemplates } from "../../Obj2Tab/NotifyTemplates/apiNotifyTemplates";
import { loadObjReqAttributes } from "../../Obj2Tab/RequiredAttributes/apiRequiredAttributes";
import { ZLightObject } from "../../Obj2Tab/Obj2Nodes";
import { deleteObject } from "../../objectsApi";
import { ObjectNode, unsavedId } from "../model/node";
import {
  actionsOf,
  allowedActions,
  summaryActions,
} from "../model/permissions";
import { childNodesOf } from "./childNodes";
import { renderNodeTitle } from "./title";
import { saveObjectNode } from "./objectSave";
import {
  KindContext,
  LazyKindHandler,
  RemovableKindHandler,
  SavableKindHandler,
  requireDetails,
} from "./types";

/**
 * Поля объекта для формы и проекции: полные, когда детали загружены,
 * иначе то немногое, что известно из каталога.
 */
export const objectFields = (
  node: ObjectNode,
  ctx: KindContext,
): ZLightObject => {
  const details = ctx.details(node.data.id);
  if (!details) {
    const { id, name, displayId } = node.data;
    return { id, name, displayId };
  }
  const { attributes, groups, ...fields } = details;
  return fields;
};

export const objectKind: LazyKindHandler<ObjectNode> &
  RemovableKindHandler<ObjectNode> &
  SavableKindHandler<ObjectNode> = {
  title: (node, actions) =>
    renderNodeTitle({
      name: node.data.name,
      id: node.data.id,
      displayId: node.data.displayId,
      disabled: !actions.update,
      placeholder: "Новый объект",
    }),

  icon: () => <ProductOutlined />,

  ownActions: (node, ctx) => {
    // Несохранённый объект существует только на клиенте, сервер о нём ещё не
    // знает - ограничивать его нечем. Иначе форма считает объект защищённым
    // и не даёт ввести название, то есть создать объект вообще нельзя.
    if (node.data.id === unsavedId) return allowedActions;
    const details = ctx.details(node.data.id);
    // Пока деталей нет, права известны только по флагам каталога
    return details ? actionsOf(details) : summaryActions(node.data);
  },

  isExpandable: () => true,

  children: async (node, ctx) => {
    const details = requireDetails(ctx, node.data.id);
    return childNodesOf(node.id, details.attributes, details.groups);
  },

  formData: async (node, ctx) => {
    const objectId = node.data.id;
    const isNew = objectId === unsavedId;
    const states = isNew ? undefined : ctx.states(objectId);
    const translation = await loadTranslation(node.data.name);
    const rolesMap = states ? await makeRolesMap(objectId, states) : undefined;
    const templatesMap = await loadNotifyTemplates(objectId);
    const delayedNotifyMap = await loadDelayedNotify(objectId);
    const requiredAttrsMap = isNew ? {} : await loadObjReqAttributes(objectId);
    const fields = objectFields(node, ctx);
    return obj2edit(
      fields,
      states,
      rolesMap,
      translation.translations,
      templatesMap,
      delayedNotifyMap,
      requiredAttrsMap,
      fields.disableVersionAudit ?? false,
    );
  },

  removeMessage: (node) =>
    node.data.name ? `Удалить объект "${node.data.name}"` : "Удалить объект?",

  // Несохранённый объект существует только на клиенте: серверу его не отдавали
  remove: async (node) => {
    if (node.data.id !== unsavedId) await deleteObject(node.data.id);
  },

  save: saveObjectNode,
};
